/**
 * Legible text on a colour somebody else chose.
 *
 * ── Why this is computed rather than written down ───────────────────────────
 *
 * Status and priority colours are data, not brand: the backend sends its own
 * colour per status, and an administrator can change one. The design system's
 * rule — "filled status chips carry near-black text, several of the colours are
 * too light for white" — is true of most of them and wrong about at least one.
 * Measured against the seven seeded statuses at 14px:
 *
 *     backlog      #8a8f98   near-black 5.10   white 3.25
 *     to do        #4650db   near-black 2.71   white 6.12   ← near-black fails
 *     in progress  #2f86e0   near-black 4.43   white 3.75   ← neither passes
 *     blocked      #e04b3c   near-black 4.14   white 4.01   ← neither passes
 *     in review    #e9a13b   near-black 7.60   white 2.18
 *     done         #12b886   near-black 6.49   white 2.55
 *     cancelled    #9e9e9e   near-black 6.19   white 2.68
 *
 * "To Do" was rendering near-black on indigo at 2.71:1, which is not a
 * readability preference — it is text nobody can read. And since the colour can
 * arrive from the database, no hardcoded ink can be right for all of them.
 *
 * ── What it does when neither ink is enough ─────────────────────────────────
 *
 * Two of the seven fail with either colour. Rather than accept 4.14:1, the
 * BACKGROUND is walked toward the chosen ink in small steps until the pair
 * passes. The hue survives, the chip is still recognisably that status, and the
 * label becomes readable. The adjustment is only ever applied where text sits on
 * the colour — a board dot or a priority marker keeps the exact value it was
 * given, because there is nothing to read on it.
 */

/** WCAG relative luminance. */
function channel(value) {
  const c = value / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

function parseColor(color) {
  const hex = String(color || "").trim()
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hex)
  if (short) return [short[1], short[2], short[3]].map((h) => parseInt(h + h, 16))
  const long = /^#([0-9a-f]{6})$/i.exec(hex)
  if (long) {
    const n = parseInt(long[1], 16)
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  }
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(hex)
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])]
  return null
}

export function luminance(color) {
  const rgb = parseColor(color)
  if (!rgb) return null
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2])
}

/** The WCAG ratio between two colours, 1 to 21. Null if either cannot be read. */
export function contrastRatio(a, b) {
  const [x, y] = [luminance(a), luminance(b)]
  if (x == null || y == null) return null
  const [hi, lo] = x > y ? [x, y] : [y, x]
  return (hi + 0.05) / (lo + 0.05)
}

/** The app's two inks. Near-black is the brand ink, not pure black. */
export const INK_DARK = "#10230a"
export const INK_LIGHT = "#ffffff"

/** AA for body text. Chip labels are 10–12px, so this is the right bar, not 3:1. */
export const AA_NORMAL = 4.5

function toHex([r, g, b]) {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`
}

/**
 * `color`, moved a fraction of the way toward `target`.
 *
 * The same arithmetic as `mix`, exported because a gradient needs the second
 * stop of a colour it was handed — a monogram tile, a hover state, a chart
 * band — and every caller writing its own hex maths is how a palette stops
 * being a palette.
 */
export function shadeToward(color, target, amount = 0.3) {
  return mix(color, target, amount)
}

/** One step of `background` toward `target`. */
function mix(background, target, amount) {
  const a = parseColor(background)
  const b = parseColor(target)
  if (!a || !b) return background
  return toHex(a.map((v, i) => v + (b[i] - v) * amount))
}

/**
 * `{ bg, ink, ratio }` — a background and a text colour that can be read on it.
 *
 * `bg` comes back unchanged whenever one of the two inks already clears the bar,
 * which is five of the seven seeded statuses.
 *
 * When neither does, the background is walked AWAY from the ink — deepening a
 * blue under white, lightening it under near-black — because moving it toward
 * the ink is what made them illegible in the first place. Both directions are
 * tried and the one that gets there in fewer steps wins, so the chip stays as
 * close to the colour it was given as legibility allows.
 */
export function readableOn(background, { minimum = AA_NORMAL } = {}) {
  if (!parseColor(background)) return { bg: background, ink: INK_DARK, ratio: null }

  const dark = contrastRatio(background, INK_DARK)
  const light = contrastRatio(background, INK_LIGHT)
  if (Math.max(dark, light) >= minimum) {
    const ink = dark >= light ? INK_DARK : INK_LIGHT
    return { bg: background, ink, ratio: Math.max(dark, light) }
  }

  const STEP = 0.06
  const MAX_STEPS = 14

  /** Push the background away from `ink` until the pair is readable. */
  const deepen = (ink) => {
    const away = ink === INK_DARK ? INK_LIGHT : INK_DARK
    let bg = background
    let ratio = contrastRatio(bg, ink)
    let steps = 0
    while (ratio < minimum && steps < MAX_STEPS) {
      bg = mix(bg, away, STEP)
      ratio = contrastRatio(bg, ink)
      steps += 1
    }
    return { bg, ink, ratio, steps }
  }

  const onDark = deepen(INK_DARK)
  const onLight = deepen(INK_LIGHT)

  // Fewest steps keeps the hue closest to what was asked for. A candidate that
  // never got there loses regardless of how few steps it took.
  const reached = [onDark, onLight].filter((c) => c.ratio >= minimum)
  if (reached.length === 0) return onDark.ratio >= onLight.ratio ? onDark : onLight
  return reached.sort((a, b) => a.steps - b.steps || b.ratio - a.ratio)[0]
}

/**
 * `sx` for a chip filled with a colour from the database.
 *
 * One call site's worth of convenience, used everywhere a status, a priority or
 * a project colour is rendered as a filled pill — so a colour added to the
 * catalogue next year is legible without anyone remembering this file exists.
 */
export function filledChipSx(background, fallback = "#8a8f98") {
  const { bg, ink } = whiteInkOn(background || fallback)
  return { bgcolor: bg, color: ink }
}

/**
 * `{ bg, ink, ratio }` with white ink, deepening the fill until it reads.
 *
 * ── Why white, rather than whichever ink is nearer ──────────────────────────
 *
 * `readableOn` picks the ink that needs the smaller adjustment, and for "In
 * Progress" blue that was near-black on a slightly lightened blue: legible by
 * the ratio, and read by everybody who looked at it as a chip that had failed
 * to render. The platform's rule for anything filled — buttons, the brand
 * green, toggles — is white on a fill deep enough to carry it, and a status
 * chip that breaks the rule is the one that looks wrong.
 *
 * So the fill is walked toward black, not toward the brand ink: black keeps the
 * hue (a darker blue is still blue; blue mixed with a green-black goes teal).
 * Seeded statuses land here as: In Progress and Blocked two steps darker,
 * Done five (about the green the rest of the app already uses under white),
 * In Review seven, from amber to an ochre — the largest change of the seven.
 */
export function whiteInkOn(background, { minimum = AA_NORMAL } = {}) {
  if (!parseColor(background)) return { bg: background, ink: INK_LIGHT, ratio: null }
  let bg = background
  let ratio = contrastRatio(bg, INK_LIGHT)
  for (let steps = 0; ratio < minimum && steps < 24; steps += 1) {
    bg = mix(bg, "#000000", 0.06)
    ratio = contrastRatio(bg, INK_LIGHT)
  }
  return { bg, ink: INK_LIGHT, ratio }
}

/**
 * A data colour, made readable as TEXT on a given surface.
 *
 * The outlined priority chips were the other half of the filled-chip problem,
 * and worse: measured as 11px text on white paper, every one of the four failed.
 *
 *     low     #8a8f98  3.25    high    #e9a13b  2.18
 *     medium  #2f86e0  3.75    urgent  #e04b3c  4.01
 *
 * Amber at 2.18:1 is a word you cannot read. The colour is walked away from the
 * surface — darkened on white, lightened on the dark theme — until it passes,
 * which is why this needs to know what it is sitting on rather than assuming a
 * light background. On the dark surface three of the four already pass and come
 * back untouched.
 */
export function readableText(color, { on = "#ffffff", minimum = AA_NORMAL } = {}) {
  if (!parseColor(color) || !parseColor(on)) return color

  let text = color
  let ratio = contrastRatio(text, on)
  if (ratio >= minimum) return text

  // Away from the surface: toward near-black on a light one, toward white on a
  // dark one. Ink is picked by the surface, not by the colour being adjusted.
  const away = luminance(on) > 0.4 ? INK_DARK : INK_LIGHT
  for (let i = 0; i < 16 && ratio < minimum; i += 1) {
    text = mix(text, away, 0.06)
    ratio = contrastRatio(text, on)
  }
  return text
}

/**
 * `sx` for an outlined chip carrying a data colour.
 *
 * The border takes the adjusted colour too rather than the original: a 1px line
 * at 2:1 against the card is a border nobody can see, and a chip whose outline
 * and label are different shades of the same hue looks like a rendering fault.
 */
export function outlinedChipSx(color, surface = "#ffffff") {
  const text = readableText(color, { on: surface })
  return { borderColor: text, color: text }
}
